import { eventsApi } from './api';

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  venue: string;
  venueId: string;
  category: string;
  price: number;
  capacity: number;
  image: string;
  status: 'draft' | 'published' | 'sold_out' | 'cancelled';
  ticketsSold: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  adminId?: string; // Track which admin created this event
}

// API-based Events Service
class EventsService {
  // Get all events (admin-specific or global)
  async getAllEvents(adminId?: string, companyName?: string): Promise<Event[]> {
    try {
      const query: any = {};
      if (companyName) {
        query.companyName = companyName;
      }
      const response = await eventsApi.getAllEvents(query);
      console.log('Events API response:', response);
      
      // API returns { success: true, data: [...events], message: "..." }
      // After axios interceptor, we get the response.data which is the API response object
      // So response = { success: true, data: [...events], message: "..." }
      if (Array.isArray(response)) {
        return response;
      }
      // Check for response.data (the events array)
      if (response && response.data && Array.isArray(response.data)) {
        return response.data;
      }
      // Fallback: check for response.events
      if (response && response.events && Array.isArray(response.events)) {
        return response.events;
      }
      console.warn('Unexpected response format:', response);
      return [];
    } catch (error) {
      console.error('Error loading events:', error);
      return [];
    }
  }

  // Get published events only (for public display)
  async getPublishedEvents(): Promise<Event[]> {
    try {
      const response = await eventsApi.getPublishedEvents();
      console.log('Published events API response:', response);
      
      // API returns { success: true, data: [...events], message: "..." }
      // After axios interceptor, we get the response.data which is the API response object
      if (Array.isArray(response)) {
        return response;
      }
      // Check for response.data (the events array)
      if (response && response.data && Array.isArray(response.data)) {
        return response.data;
      }
      // Fallback: check for response.events
      if (response && response.events && Array.isArray(response.events)) {
        return response.events;
      }
      console.warn('Unexpected published events response format:', response);
      return [];
    } catch (error) {
      console.error('Error loading published events:', error);
      return [];
    }
  }

  // Get event by ID
  async getEventById(id: string): Promise<Event | null> {
    try {
      const response = await eventsApi.getEventById(id);
      console.log('Get event by ID response:', response);
      
      // Handle different response formats
      if (response && response.event) {
        return response.event;
      }
      if (response && response.data) {
        return response.data;
      }
      if (response && typeof response === 'object' && response.id) {
        return response as Event;
      }
      
      console.warn('Unexpected event response format:', response);
      return null;
    } catch (error) {
      console.error('Error loading event:', error);
      return null;
    }
  }

  // Add new event
  async addEvent(eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'ticketsSold' | 'adminId'>): Promise<Event> {
    try {
      const response = await eventsApi.createEvent(eventData);
      // Handle both response formats: { event: ... } or just the event object
      const event = response.event || response.data?.event || response;
      if (!event) {
        throw new Error('Invalid response format from server');
      }
      return event;
    } catch (error: any) {
      console.error('Error creating event:', error);
      const errorMessage = error?.message || error?.response?.data?.message || error?.response?.data?.errors?.[0]?.message || 'Failed to create event';
      throw new Error(errorMessage);
    }
  }

  // Update existing event
  async updateEvent(id: string, eventData: Partial<Event>): Promise<Event | null> {
    try {
      // Convert to API format
      const apiData: any = {};
      
      // Copy all fields except status
      Object.keys(eventData).forEach(key => {
        if (key !== 'status') {
          apiData[key] = (eventData as any)[key];
        }
      });
      
      // Convert status to uppercase if present
      if (eventData.status) {
        apiData.status = eventData.status.toUpperCase();
      }
      
      const response = await eventsApi.updateEvent(id, apiData);
      return response.event;
    } catch (error) {
      console.error('Error updating event:', error);
      return null;
    }
  }

  // Delete event
  async deleteEvent(id: string): Promise<boolean> {
    try {
      await eventsApi.deleteEvent(id);
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      return false;
    }
  }

  // Publish event
  async publishEvent(id: string): Promise<Event | null> {
    try {
      const response = await eventsApi.publishEvent(id);
      return response.event;
    } catch (error) {
      console.error('Error publishing event:', error);
      return null;
    }
  }

  // Set event as sold out
  async setSoldOut(id: string): Promise<Event | null> {
    return this.updateEvent(id, { status: 'sold_out' });
  }

  // Update ticket sales
  async updateTicketSales(id: string, ticketsSold: number): Promise<Event | null> {
    const event = await this.getEventById(id);
    if (event && ticketsSold <= event.capacity) {
      const status = ticketsSold >= event.capacity ? 'sold_out' : event.status;
      return this.updateEvent(id, { ticketsSold, status });
    }
    return null;
  }

  // Get events by category
  async getEventsByCategory(category: string): Promise<Event[]> {
    const events = await this.getPublishedEvents();
    if (category === 'all') return events;
    return events.filter(event => event.category.toLowerCase() === category.toLowerCase());
  }

  // Search events
  async searchEvents(query: string): Promise<Event[]> {
    const events = await this.getPublishedEvents();
    const searchQuery = query.toLowerCase();
    
    return events.filter(event => 
      event.title.toLowerCase().includes(searchQuery) ||
      event.description.toLowerCase().includes(searchQuery) ||
      event.venue.toLowerCase().includes(searchQuery) ||
      event.category.toLowerCase().includes(searchQuery) ||
      event.tags.some(tag => tag.toLowerCase().includes(searchQuery))
    );
  }

  // Get event statistics
  async getEventStats() {
    try {
      const events = await this.getAllEvents();
      const publishedEvents = events.filter(e => e.status === 'published');
      
      const totalTicketsSold = events.reduce((sum, event) => sum + event.ticketsSold, 0);
      const totalRevenue = events.reduce((sum, event) => sum + (event.ticketsSold * event.price), 0);
      const averagePrice = events.length > 0 
        ? events.reduce((sum, event) => sum + event.price, 0) / events.length 
        : 0;

      return {
        totalEvents: events.length,
        publishedEvents: publishedEvents.length,
        draftEvents: events.filter(e => e.status === 'draft').length,
        soldOutEvents: events.filter(e => e.status === 'sold_out').length,
        totalTicketsSold,
        totalRevenue,
        averagePrice: Math.round(averagePrice * 100) / 100
      };
    } catch (error) {
      console.error('Error getting event stats:', error);
      return {
        totalEvents: 0,
        publishedEvents: 0,
        draftEvents: 0,
        soldOutEvents: 0,
        totalTicketsSold: 0,
        totalRevenue: 0,
        averagePrice: 0
      };
    }
  }

  // Force refresh events (no-op for API-based service)
  refreshEventsWithNewData(): void {
    // This method is kept for backward compatibility
    // In the API-based version, data is always fresh from the server
    console.log('refreshEventsWithNewData called - data always fresh from API');
  }
}

export const eventsService = new EventsService();
export type { Event };
